/**
 * Mixed Service Timetable Visualization Component
 * 
 * Displays a timeline/Gantt chart showing vehicle assignments for mixed fleets
 * (both fixed routes and on-demand periods). Shows distinct visual styles for
 * each service type and transitions between them.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  ButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from '@mui/material';

import { Vehicle } from '../../../../types/vehicle';
import { OperatingPeriod, BreakPeriod } from '../../../../types/simulation';
import { RouteAssignment } from '../../../../types/fixedRoutes';
import {
  parseTime,
  formatTime,
  calculateTimeDifference,
  getTimeLabels,
} from './timeUtils';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
interface MixedServiceTimetableVisualizationProps {
  vehicles: Vehicle[];
  /** Start time in minutes from midnight (e.g., 360 for 06:00) */
  serviceStartMinutes: number;
  /** End time in minutes from midnight (e.g., 1320 for 22:00) */
  serviceEndMinutes: number;
  /** Time interval for blocks (e.g., 30 for 30-minute blocks) */
  intervalMinutes?: number;
  interactive?: boolean;
  onBlockClick?: (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => void;
}

type FilterType = 'all' | 'fixed-only' | 'on-demand-only' | 'mixed';

/**
 * Generate a consistent color for a route based on its ID
 */
function getRouteColor(routeId: string, index: number): string {
  const colors = [
    '#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336',
    '#00bcd4', '#ff5722', '#795548', '#607d8b', '#e91e63',
  ];
  return colors[index % colors.length];
}

/**
 * Check if a time block overlaps with an assignment period
 */
function blockOverlapsAssignment(
  blockStartMinutes: number,
  blockEndMinutes: number,
  periodStartMinutes: number,
  periodEndMinutes: number
): boolean {
  // Normalize times to handle overnight periods
  let normalizedBlockStart = blockStartMinutes;
  let normalizedBlockEnd = blockEndMinutes;
  let normalizedPeriodStart = periodStartMinutes;
  let normalizedPeriodEnd = periodEndMinutes;

  // Handle overnight periods (period end < period start)
  if (normalizedPeriodEnd < normalizedPeriodStart) {
    // If block is before midnight and period crosses midnight
    if (normalizedBlockStart < normalizedPeriodEnd) {
      normalizedBlockEnd = normalizedBlockEnd + (24 * 60);
    }
    normalizedPeriodEnd = normalizedPeriodEnd + (24 * 60);
  }

  // Check overlap
  return normalizedBlockStart < normalizedPeriodEnd &&
         normalizedBlockEnd > normalizedPeriodStart;
}

/**
 * Determine service type for a vehicle period
 */
function getServiceType(period: OperatingPeriod): 'fixed' | 'on-demand' | 'mixed' {
  if (period.serviceType === 'mixed') {
    return 'mixed';
  }
  if (period.serviceType === 'on-demand') {
    return 'on-demand';
  }
  if (period.routeAssignments && period.routeAssignments.length > 0) {
    return 'fixed';
  }
  // Default to on-demand if no route assignments
  return 'on-demand';
}

/**
 * Check if a vehicle serves multiple service types
 */
function isMixedVehicle(vehicle: Vehicle): boolean {
  const serviceTypes = new Set<string>();
  vehicle.operatingHours.forEach((period) => {
    serviceTypes.add(getServiceType(period));
  });
  return serviceTypes.size > 1;
}

const MixedServiceTimetableVisualization: React.FC<MixedServiceTimetableVisualizationProps> = ({
  vehicles,
  serviceStartMinutes,
  serviceEndMinutes,
  intervalMinutes = 30,
  interactive = false,
  onBlockClick,
}) => {
  const { t } = useTranslation('mapWizards');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [expandedVehicles, setExpandedVehicles] = useState<Set<string>>(new Set());

  // Toggle vehicle expansion
  const toggleVehicleExpansion = (vehicleId: string) => {
    setExpandedVehicles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  // Filter vehicles based on filter type
  const filteredVehicles = useMemo(() => {
    switch (filterType) {
      case 'fixed-only':
        return vehicles.filter(v => !isMixedVehicle(v) && 
          v.operatingHours.some(p => getServiceType(p) === 'fixed'));
      case 'on-demand-only':
        return vehicles.filter(v => !isMixedVehicle(v) && 
          v.operatingHours.some(p => getServiceType(p) === 'on-demand'));
      case 'mixed':
        return vehicles.filter(v => isMixedVehicle(v));
      default:
        return vehicles;
    }
  }, [vehicles, filterType]);

  // Group vehicles by base name (same vehicle, different shifts)
  const groupedVehicles = useMemo(() => {
    const groups = new Map<string, Vehicle[]>();
    filteredVehicles.forEach((vehicle) => {
      const baseName = vehicle.name.replace(/\s*-\s*Shift\s+\d+$/, '');
      if (!groups.has(baseName)) {
        groups.set(baseName, []);
      }
      groups.get(baseName)!.push(vehicle);
    });
    return Array.from(groups.entries());
  }, [filteredVehicles]);

  // Collect all unique routes for color assignment
  const routeMap = useMemo(() => {
    const map = new Map<string, { name: string; color: string }>();
    vehicles.forEach((vehicle) => {
      vehicle.operatingHours.forEach((period) => {
        if (period.routeAssignments) {
          period.routeAssignments.forEach((assignment: RouteAssignment, idx: number) => {
            if (!map.has(assignment.routeId)) {
              map.set(assignment.routeId, {
                name: assignment.routeName,
                color: getRouteColor(assignment.routeId, map.size),
              });
            }
          });
        }
      });
    });
    return map;
  }, [vehicles]);

  // Generate time labels for the header
  const timeLabels = getTimeLabels(serviceStartMinutes, serviceEndMinutes, intervalMinutes);
  const totalMinutes = calculateTimeDifference(serviceStartMinutes, serviceEndMinutes);
  const numBlocks = Math.ceil(totalMinutes / intervalMinutes);

  // Get assignment information for a specific time block
  const getBlockAssignment = (
    vehicle: Vehicle,
    blockStartMinutes: number
  ): {
    assignment?: RouteAssignment;
    period: OperatingPeriod;
    serviceType: 'fixed' | 'on-demand' | 'mixed';
    isBreak?: boolean;
    breakInfo?: { start: string; end: string; duration: number };
  } | null => {
    const blockEndMinutes = blockStartMinutes + intervalMinutes;

    // Check all operating periods for this vehicle
    for (const period of vehicle.operatingHours) {
      const periodStart = parseTime(period.start);
      let periodEnd = parseTime(period.end);
      
      // Handle overnight periods
      if (periodEnd < periodStart) {
        periodEnd = periodEnd + (24 * 60);
      }

      // Check if block overlaps with operating period
      if (blockOverlapsAssignment(blockStartMinutes, blockEndMinutes, periodStart, periodEnd)) {
        const serviceType = getServiceType(period);
        
        // First, check if this block overlaps with a break period
        if (period.breaks && period.breaks.length > 0) {
          for (const breakPeriod of period.breaks) {
            const breakStart = parseTime(breakPeriod.start);
            let breakEnd = parseTime(breakPeriod.end);
            
            if (breakEnd < breakStart) {
              breakEnd = breakEnd + (24 * 60);
            }

            if (blockOverlapsAssignment(blockStartMinutes, blockEndMinutes, breakStart, breakEnd)) {
              const breakDuration = calculateTimeDifference(breakStart, breakEnd);
              return {
                period,
                serviceType,
                isBreak: true,
                breakInfo: {
                  start: breakPeriod.start,
                  end: breakPeriod.end,
                  duration: breakDuration,
                },
                assignment: period.routeAssignments?.[0],
              };
            }
          }
        }

        // Not a break, return assignment info
        return {
          assignment: period.routeAssignments?.[0],
          period,
          serviceType,
          isBreak: false,
        };
      }
    }

    return null;
  };

  // Get aggregated assignment across all vehicles in a group (for collapsed view)
  const getAggregatedBlockAssignment = (
    vehicleGroup: Vehicle[],
    blockStartMinutes: number
  ): {
    assignment?: RouteAssignment;
    period: OperatingPeriod;
    serviceType: 'fixed' | 'on-demand' | 'mixed';
    isBreak?: boolean;
    breakInfo?: { start: string; end: string; duration: number };
  } | null => {
    const blockEndMinutes = blockStartMinutes + intervalMinutes;
    let bestMatch: {
      assignment?: RouteAssignment;
      period: OperatingPeriod;
      serviceType: 'fixed' | 'on-demand' | 'mixed';
      isBreak?: boolean;
      breakInfo?: { start: string; end: string; duration: number };
      priority: number; // Higher priority = shown first (breaks > fixed > on-demand)
    } | null = null;

    // Check all vehicles in the group
    for (const vehicle of vehicleGroup) {
      for (const period of vehicle.operatingHours) {
        const periodStart = parseTime(period.start);
        let periodEnd = parseTime(period.end);
        
        // Handle overnight periods
        if (periodEnd < periodStart) {
          periodEnd = periodEnd + (24 * 60);
        }

        // Check if block overlaps with operating period
        if (blockOverlapsAssignment(blockStartMinutes, blockEndMinutes, periodStart, periodEnd)) {
          const serviceType = getServiceType(period);
          
          // First, check if this block overlaps with a break period (breaks have highest priority)
          if (period.breaks && period.breaks.length > 0) {
            for (const breakPeriod of period.breaks) {
              const breakStart = parseTime(breakPeriod.start);
              let breakEnd = parseTime(breakPeriod.end);
              
              if (breakEnd < breakStart) {
                breakEnd = breakEnd + (24 * 60);
              }

              if (blockOverlapsAssignment(blockStartMinutes, blockEndMinutes, breakStart, breakEnd)) {
                const breakDuration = calculateTimeDifference(breakStart, breakEnd);
                const match = {
                  period,
                  serviceType,
                  isBreak: true,
                  breakInfo: {
                    start: breakPeriod.start,
                    end: breakPeriod.end,
                    duration: breakDuration,
                  },
                  assignment: period.routeAssignments?.[0],
                  priority: 3, // Breaks have highest priority
                };
                
                // If we find a break, always return it (highest priority)
                return match;
              }
            }
          }

          // Not a break, check if this is better than current match
          const priority = serviceType === 'fixed' ? 2 : serviceType === 'mixed' ? 1.5 : 1;
          
          if (!bestMatch || priority > bestMatch.priority) {
            bestMatch = {
              assignment: period.routeAssignments?.[0],
              period,
              serviceType,
              isBreak: false,
              priority,
            };
          }
        }
      }
    }

    return bestMatch;
  };

  // Calculate vehicles serving each route/service type per time block
  const getServiceActivity = (blockStartMinutes: number): {
    fixedRoutes: Map<string, number>;
    onDemand: number;
    mixed: number;
  } => {
    const fixedRoutes = new Map<string, number>();
    let onDemand = 0;
    let mixed = 0;
    
    vehicles.forEach((vehicle) => {
      const blockAssignment = getBlockAssignment(vehicle, blockStartMinutes);
      if (blockAssignment) {
        if (blockAssignment.isBreak) {
          // Breaks don't count toward service activity
          return;
        }
        
        if (blockAssignment.serviceType === 'fixed' && blockAssignment.assignment) {
          const routeId = blockAssignment.assignment.routeId;
          fixedRoutes.set(routeId, (fixedRoutes.get(routeId) || 0) + 1);
        } else if (blockAssignment.serviceType === 'on-demand') {
          onDemand++;
        } else if (blockAssignment.serviceType === 'mixed') {
          mixed++;
        }
      }
    });
    
    return { fixedRoutes, onDemand, mixed };
  };

  const getTimelineStatus = (
    blockAssignment: ReturnType<typeof getBlockAssignment> | ReturnType<typeof getAggregatedBlockAssignment>
  ): 'working' | 'break' | 'off' => {
    if (!blockAssignment) return 'off';
    if (blockAssignment.isBreak) return 'break';
    return 'working';
  };

  const handleBlockInteraction = (
    vehicleId: string,
    blockStartMinutes: number,
    status: 'working' | 'break' | 'off'
  ) => {
    if (!interactive || !onBlockClick) return;
    onBlockClick(vehicleId, blockStartMinutes, status);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {t('autoFleetGen.timeline.mixedTitle')}
        </Typography>

        {/* Filter Controls */}
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={() => setFilterType('all')}
            variant={filterType === 'all' ? 'contained' : 'outlined'}
          >
            {t('autoFleetGen.timeline.filterAll')}
          </Button>
          <Button
            onClick={() => setFilterType('fixed-only')}
            variant={filterType === 'fixed-only' ? 'contained' : 'outlined'}
          >
            {t('autoFleetGen.timeline.filterFixed')}
          </Button>
          <Button
            onClick={() => setFilterType('on-demand-only')}
            variant={filterType === 'on-demand-only' ? 'contained' : 'outlined'}
          >
            {t('autoFleetGen.timeline.filterOnDemand')}
          </Button>
          <Button
            onClick={() => setFilterType('mixed')}
            variant={filterType === 'mixed' ? 'contained' : 'outlined'}
          >
            {t('autoFleetGen.timeline.filterMixed')}
          </Button>
        </ButtonGroup>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={3} mb={2} flexWrap="wrap" alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            width={20}
            height={20}
            bgcolor="#4caf50"
            borderRadius={1}
            border="1px solid #2e7d32"
          />
          <Typography variant="caption">{t('autoFleetGen.timeline.fixedRoute')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            width={20}
            height={20}
            bgcolor="#2196f3"
            borderRadius={1}
            border="1px solid #1565c0"
          />
          <Typography variant="caption">{t('autoFleetGen.timeline.onDemand')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            width={20}
            height={20}
            bgcolor="#ffc107"
            borderRadius={1}
            border="1px solid #f57c00"
          />
          <Typography variant="caption">{t('autoFleetGen.timeline.break')}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            width={20}
            height={20}
            bgcolor="#e0e0e0"
            borderRadius={1}
            border="1px solid #bdbdbd"
          />
          <Typography variant="caption">{t('autoFleetGen.timeline.idle')}</Typography>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ overflow: 'auto', maxHeight: '70vh' }}>
        <Box>
          {/* Time header */}
          <Box
            display="flex"
            position="sticky"
            top={0}
            bgcolor="background.paper"
            zIndex={10}
            borderBottom="2px solid #e0e0e0"
          >
            <Box
              minWidth={150}
              p={1}
              borderRight="1px solid #e0e0e0"
              bgcolor="#f5f5f5"
              display="flex"
              alignItems="center"
            >
              <Typography variant="caption" fontWeight={600}>
                {t('autoFleetGen.timeline.vehicleHeader')}
              </Typography>
            </Box>
            {timeLabels.map((label, idx) => (
              <Box
                key={idx}
                minWidth={80}
                width={80}
                p={1}
                borderRight="1px solid #e0e0e0"
                textAlign="center"
                bgcolor="#f5f5f5"
                boxSizing="border-box"
              >
                <Typography variant="caption" fontWeight={600}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Vehicle rows - grouped by base name */}
          {groupedVehicles.map(([baseName, vehicleGroup], groupIdx) => {
            const firstVehicle = vehicleGroup[0];
            const isExpanded = expandedVehicles.has(firstVehicle.id);
            const hasMultipleShifts = vehicleGroup.length > 1;
            const isMixed = vehicleGroup.some(v => isMixedVehicle(v));
            
            const vehicleGroupIndex = groupIdx;
            const vehicleColor = `hsl(${(vehicleGroupIndex * 137.5) % 360}, 70%, 92%)`;
            const vehicleBorderColor = `hsl(${(vehicleGroupIndex * 137.5) % 360}, 50%, 70%)`;

            return (
              <Box key={baseName}>
                {/* Main vehicle row header (clickable) */}
                <Box
                  display="flex"
                  bgcolor={vehicleColor}
                  borderBottom="1px solid #e0e0e0"
                  sx={{
                    cursor: hasMultipleShifts ? 'pointer' : 'default',
                    '&:hover': hasMultipleShifts ? { bgcolor: `${vehicleColor}dd` } : {},
                  }}
                  onClick={() => hasMultipleShifts && toggleVehicleExpansion(firstVehicle.id)}
                >
                  <Box
                    minWidth={150}
                    p={1}
                    borderRight="1px solid #e0e0e0"
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Box
                      width={4}
                      height={40}
                      bgcolor={vehicleBorderColor}
                      borderRadius="2px"
                    />
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {baseName}
                        </Typography>
                        {hasMultipleShifts && (
                          <Box>
                            {isExpanded ? (
                              <Icon name="chevron-up" size={20} />
                            ) : (
                              <Icon name="chevron-down" size={20} />
                            )}
                          </Box>
                        )}
                      </Box>
                      {isMixed && (
                        <Chip
                          label={t('autoFleetGen.timeline.mixedChip')}
                          size="small"
                          icon={<Icon size={20} d={ICON_FALLBACKS.SwapHoriz} />}
                          sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                          color="warning"
                        />
                      )}
                      {hasMultipleShifts && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {t(
                            vehicleGroup.length === 1
                              ? 'autoFleetGen.timeline.shiftsClickToExpandOne'
                              : 'autoFleetGen.timeline.shiftsClickToExpandOther',
                            { total: vehicleGroup.length }
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Time blocks - aggregated view when collapsed, first vehicle when expanded */}
                  {Array.from({ length: numBlocks }).map((_, blockIdx) => {
                    const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
                    // Get assignment: aggregated across all vehicles if collapsed, first vehicle if expanded
                    const blockAssignment = hasMultipleShifts && !isExpanded
                      ? getAggregatedBlockAssignment(vehicleGroup, blockStartMinutes)
                      : getBlockAssignment(firstVehicle, blockStartMinutes);
                    
                    if (!blockAssignment) {
                      return (
                        <Box
                          key={blockIdx}
                          minWidth={80}
                          width={80}
                          height={60}
                          bgcolor="#fafafa"
                          borderRight="1px solid #e0e0e0"
                          boxSizing="border-box"
                          role={interactive ? 'button' : undefined}
                          tabIndex={interactive ? 0 : -1}
                          onClick={() => handleBlockInteraction(firstVehicle.id, blockStartMinutes, 'off')}
                          onKeyDown={(event) => {
                            if (!interactive) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleBlockInteraction(firstVehicle.id, blockStartMinutes, 'off');
                            }
                          }}
                        />
                      );
                    }

                    const { assignment, period, serviceType, isBreak, breakInfo } = blockAssignment;
                    
                    let blockColor = '#e0e0e0';
                    let blockBorderColor = '#bdbdbd';
                    let blockLabel = '';
                    
                    if (isBreak) {
                      blockColor = '#ffc107';
                      blockBorderColor = '#f57c00';
                      blockLabel = t('autoFleetGen.timeline.break');
                    } else if (serviceType === 'fixed' && assignment) {
                      const routeInfo = routeMap.get(assignment.routeId);
                      blockColor = routeInfo?.color || '#4caf50';
                      blockBorderColor = '#2e7d32';
                      blockLabel = assignment.routeName || assignment.routeId;
                    } else if (serviceType === 'on-demand') {
                      blockColor = '#2196f3';
                      blockBorderColor = '#1565c0';
                      blockLabel = t('autoFleetGen.timeline.onDemand');
                    } else if (serviceType === 'mixed') {
                      blockColor = '#ff9800';
                      blockBorderColor = '#e65100';
                      blockLabel = t('autoFleetGen.timeline.mixedChip');
                    }

                    return (
                      <Tooltip
                        key={blockIdx}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {hasMultipleShifts && !isExpanded
                                ? t('autoFleetGen.timeline.aggregatedTitle', { name: baseName, count: vehicleGroup.length })
                                : blockLabel}
                            </Typography>
                            {blockAssignment && (
                              <>
                                <Typography variant="caption">
                                  {blockAssignment.period.start} - {blockAssignment.period.end}
                                  {hasMultipleShifts && !isExpanded && t('autoFleetGen.timeline.fromAllShifts')}
                                </Typography>
                                {hasMultipleShifts && !isExpanded && (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {t('autoFleetGen.timeline.clickToBreakdown')}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Box
                          minWidth={80}
                          width={80}
                          height={60}
                          bgcolor={blockColor}
                          border={`2px solid ${blockBorderColor}`}
                          borderRight="1px solid #e0e0e0"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          boxSizing="border-box"
                          sx={{
                            cursor: interactive || hasMultipleShifts ? 'pointer' : 'default',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={(e) => {
                            if (interactive) {
                              e.stopPropagation();
                              handleBlockInteraction(firstVehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment));
                              return;
                            }
                            if (hasMultipleShifts) {
                              e.stopPropagation();
                              toggleVehicleExpansion(firstVehicle.id);
                            }
                          }}
                          role={interactive ? 'button' : undefined}
                          tabIndex={interactive ? 0 : -1}
                          onKeyDown={(event) => {
                            if (!interactive) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleBlockInteraction(firstVehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment));
                            }
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: isBreak ? '#000' : '#fff',
                              textAlign: 'center',
                              width: '100%',
                              px: 0.5,
                            }}
                          >
                            {blockLabel}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>

                {/* Expanded shifts (accordion content) */}
                {hasMultipleShifts && (
                  <Collapse in={isExpanded}>
                    <Box>
                      {vehicleGroup.map((vehicle, shiftIdx) => (
                        <Box
                          key={`${vehicle.id}-shift-${shiftIdx}`}
                          display="flex"
                          bgcolor="#f9f9f9"
                          borderBottom="1px solid #e0e0e0"
                          borderLeft={`4px solid ${vehicleBorderColor}`}
                        >
                          <Box
                            minWidth={150}
                            p={1}
                            borderRight="1px solid #e0e0e0"
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Box>
                              <Typography variant="caption" fontWeight={600} color="text.secondary">
                                {t('autoFleetGen.timeline.shiftLabel', { value: shiftIdx + 1 })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                {vehicle.operatingHours[0]?.start || ''} - {vehicle.operatingHours[0]?.end || ''}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Time blocks for this shift */}
                          {Array.from({ length: numBlocks }).map((_, blockIdx) => {
                            const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
                            const blockAssignment = getBlockAssignment(vehicle, blockStartMinutes);
                            
                            if (!blockAssignment) {
                              return (
                                <Box
                                  key={blockIdx}
                                  minWidth={80}
                                  width={80}
                                  height={60}
                                  bgcolor="#fafafa"
                                  borderRight="1px solid #e0e0e0"
                                  boxSizing="border-box"
                                />
                              );
                            }

                            const { assignment, period, serviceType, isBreak, breakInfo } = blockAssignment;
                            
                            let blockColor = '#e0e0e0';
                            let blockBorderColor = '#bdbdbd';
                            let blockLabel = '';
                            
                            if (isBreak) {
                              blockColor = '#ffc107';
                              blockBorderColor = '#f57c00';
                              blockLabel = t('autoFleetGen.timeline.break');
                            } else if (serviceType === 'fixed' && assignment) {
                              const routeInfo = routeMap.get(assignment.routeId);
                              blockColor = routeInfo?.color || '#4caf50';
                              blockBorderColor = '#2e7d32';
                              blockLabel = assignment.routeName || assignment.routeId;
                            } else if (serviceType === 'on-demand') {
                              blockColor = '#2196f3';
                              blockBorderColor = '#1565c0';
                              blockLabel = t('autoFleetGen.timeline.onDemand');
                            } else if (serviceType === 'mixed') {
                              blockColor = '#ff9800';
                              blockBorderColor = '#e65100';
                              blockLabel = t('autoFleetGen.timeline.mixedChip');
                            }

                            const tooltipText = isBreak && breakInfo
                              ? `Break: ${breakInfo.start} - ${breakInfo.end} (${breakInfo.duration} min)`
                              : serviceType === 'fixed' && assignment
                              ? `${assignment.routeName || assignment.routeId}\n${period.start} - ${period.end}`
                              : `${blockLabel}\n${period.start} - ${period.end}`;

                            return (
                              <Tooltip
                                key={blockIdx}
                                title={
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {tooltipText.split('\n')[0]}
                                    </Typography>
                                    <Typography variant="caption">
                                      {tooltipText.split('\n')[1]}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                      {t('autoFleetGen.timeline.shiftOf', { shift: shiftIdx + 1, total: vehicleGroup.length })}
                                    </Typography>
                                  </Box>
                                }
                                arrow
                              >
                                <Box
                                  minWidth={80}
                                  width={80}
                                  height={60}
                                  bgcolor={blockColor}
                                  border={`2px solid ${blockBorderColor}`}
                                  borderRight="1px solid #e0e0e0"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  boxSizing="border-box"
                                  sx={{
                                    cursor: interactive ? 'pointer' : 'default',
                                    '&:hover': {
                                      opacity: 0.8,
                                    },
                                  }}
                                  role={interactive ? 'button' : undefined}
                                  tabIndex={interactive ? 0 : -1}
                                  onClick={() =>
                                    handleBlockInteraction(vehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment))
                                  }
                                  onKeyDown={(event) => {
                                    if (!interactive) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleBlockInteraction(vehicle.id, blockStartMinutes, getTimelineStatus(blockAssignment));
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      color: isBreak ? '#000' : '#fff',
                                      textAlign: 'center',
                                      width: '100%',
                                      px: 0.5,
                                    }}
                                  >
                                    {blockLabel}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
          
          {/* Summary row */}
          <Box
            display="flex"
            bgcolor="#f5f5f5"
            borderTop="2px solid #e0e0e0"
            position="sticky"
            bottom={0}
          >
            <Box
              minWidth={150}
              p={1}
              borderRight="1px solid #e0e0e0"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Typography variant="caption" fontWeight={600}>
                {t('autoFleetGen.timeline.activitySummary')}
              </Typography>
            </Box>
            {Array.from({ length: numBlocks }).map((_, blockIdx) => {
              const blockStartMinutes = serviceStartMinutes + blockIdx * intervalMinutes;
              const activity = getServiceActivity(blockStartMinutes);
              
              return (
                <Box
                  key={blockIdx}
                  minWidth={80}
                  width={80}
                  display="flex"
                  flexDirection="row"
                  gap={1}
                  alignItems="center"
                  justifyContent="space-between"
                  boxSizing="border-box"
                  p={1}
                  sx={{
                    minHeight: 50,
                    bgcolor: '#fafafa',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  {(() => {
                    // Calculate total vehicles
                    const totalFixed = Array.from(activity.fixedRoutes.values()).reduce((sum, count) => sum + count, 0);
                    const totalVehicles = totalFixed + activity.onDemand + activity.mixed;
                    
                    if (totalVehicles === 0) {
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          -
                        </Typography>
                      );
                    }
                    
                    return (
                      <>
                        {/* Large total vehicles number on the left */}
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: '#000',
                            lineHeight: 1,
                          }}
                        >
                          {totalVehicles}
                        </Typography>
                        
                        {/* Service type breakdown on the right */}
                        <Box display="flex" flexDirection="column" gap={0.25} alignItems="flex-end">
                          {Array.from(activity.fixedRoutes.entries()).map(([routeId, count]) => {
                            const routeInfo = routeMap.get(routeId);
                            if (!routeInfo) return null;
                            
                            return (
                              <Box key={routeId} display="flex" alignItems="center" gap={0.5}>
                                <Box
                                  width={8}
                                  height={8}
                                  bgcolor={routeInfo.color}
                                  borderRadius="50%"
                                />
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                                  {count}
                                </Typography>
                              </Box>
                            );
                          })}
                          {activity.onDemand > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Box
                                width={8}
                                height={8}
                                bgcolor="#2196f3"
                                borderRadius="50%"
                              />
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                                {activity.onDemand}
                              </Typography>
                            </Box>
                          )}
                          {activity.mixed > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Box
                                width={8}
                                height={8}
                                bgcolor="#ff9800"
                                borderRadius="50%"
                              />
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                                {activity.mixed}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MixedServiceTimetableVisualization;

